import { useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Bell,
  PanelLeftClose,
  PanelLeftOpen,
  Building2,
  Calendar,
  MessageSquareWarning,
  UserPlus,
  Sun,
  Moon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCanAccess, useGetIdentity } from "ra-core";
import { ThemeProviderContext } from "@/components/admin/theme-context";
import { UserMenu } from "@/components/admin/user-menu";
import { useRealEstateContext } from "../providers/RealEstateContext";
import { ScheduleVisitDrawer } from "../site-visits/ScheduleVisitDrawer";
import { useDeskAttentionCounts } from "../contacts/useDeskAttention";

interface HeaderProps {
  isCollapsed: boolean;
  onToggleSidebar: () => void;
}

export const Header = ({ isCollapsed, onToggleSidebar }: HeaderProps) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const { siteVisits, properties, campaignAudience } = useRealEstateContext();
  const { identity } = useGetIdentity();
  const { canAccess: isAdmin } = useCanAccess({
    resource: "configuration",
    action: "edit",
  });
  // Inbox badge = unread WhatsApp contacts (desk_queue → unread_whatsapp_contact_ids RPC).
  // Do not fold in slaOverdue — that queue is unrelated to /inbox and can look "stuck"
  // near the total contact count when many leads sit idle.
  const { needsWhatsAppReply } = useDeskAttentionCounts();
  const inboxCount = needsWhatsAppReply;

  const notifications = useMemo(() => {
    const mySalesId = identity?.id;
    const upcoming = [...siteVisits]
      .filter((v) => v.status === "Scheduled")
      .filter((v) => {
        // Managers see the full desk; agents only visits assigned to them
        // (or unassigned visits they can already see via contact RLS).
        if (isAdmin) return true;
        if (mySalesId == null) return false;
        if (v.agent_id == null || v.agent_id === "") return true;
        return String(v.agent_id) === String(mySalesId);
      })
      .sort(
        (a, b) =>
          new Date(a.scheduled_at).getTime() -
          new Date(b.scheduled_at).getTime(),
      )
      .slice(0, 5);

    return upcoming.map((visit) => {
      const prop = properties.find(
        (p) => String(p.id) === String(visit.property_id),
      );
      const lead = campaignAudience.find(
        (l) => String(l.id) === String(visit.contact_id),
      );
      const unitLabel = prop?.unit_number
        ? `${prop.project?.name || "Project"} Unit ${prop.unit_number}`
        : "Site visit";
      return {
        id: visit.id,
        title: "Site visit scheduled",
        detail: `${lead?.name || `Contact #${visit.contact_id}`} · ${unitLabel} · ${new Date(visit.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}`,
      };
    });
  }, [siteVisits, properties, campaignAudience, identity?.id, isAdmin]);

  const handleGlobalSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const filterJson = JSON.stringify({ q: searchQuery.trim() });
      navigate(`/contacts?filter=${encodeURIComponent(filterJson)}`);
    }
  };

  const { theme, setTheme } = useContext(ThemeProviderContext);

  return (
    <header className="h-16 border-b bg-card px-4 flex items-center justify-between gap-4 z-20 select-none">
      {/* Left Utilities */}
      <div className="flex items-center gap-3 flex-1 max-w-xl">
        {/* Desktop Sidebar Toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 text-muted-foreground hover:text-foreground shrink-0"
          onClick={onToggleSidebar}
          title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          {isCollapsed ? (
            <PanelLeftOpen className="w-5 h-5" />
          ) : (
            <PanelLeftClose className="w-5 h-5" />
          )}
        </Button>

        {/* Global Search Bar */}
        <form
          onSubmit={handleGlobalSearch}
          className="relative w-full max-w-md"
        >
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
          <Input
            placeholder="Search leads by phone, name, or unit #..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9 text-xs bg-muted/30 focus:bg-background"
          />
        </form>
      </div>

      {/* Right Utilities */}
      <div className="flex items-center gap-3 shrink-0">
        {/* Quick Action Dropdown (+ New) */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-xs h-9 px-3 flex items-center gap-1.5 shadow-sm"
            >
              <Plus className="w-4 h-4 shrink-0" />
              <span>New</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-56 min-w-[220px] text-xs"
          >
            <DropdownMenuLabel className="font-bold text-[11px] uppercase text-muted-foreground">
              Quick Actions
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => navigate("/contacts/create")}
              className="cursor-pointer flex items-center py-2"
            >
              <UserPlus className="w-4 h-4 mr-2.5 text-primary shrink-0" />
              <span className="font-medium whitespace-nowrap">
                Add Lead Client
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setScheduleOpen(true)}
              className="cursor-pointer flex items-center py-2"
            >
              <Calendar className="w-4 h-4 mr-2.5 text-emerald-600 shrink-0" />
              <span className="font-medium whitespace-nowrap">
                Schedule Site Visit
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate("/properties?create=1")}
              className="cursor-pointer flex items-center py-2"
            >
              <Building2 className="w-4 h-4 mr-2.5 text-indigo-600 shrink-0" />
              <span className="font-medium whitespace-nowrap">
                Add Property Unit
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Inbox badge — contacts needing a WhatsApp reply */}
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 relative text-muted-foreground hover:text-foreground"
          onClick={() => navigate("/inbox")}
          title="Inbox — unread WhatsApp replies"
        >
          <MessageSquareWarning className="w-4 h-4" />
          {inboxCount > 0 ? (
            <Badge className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-red-600 text-white text-[9px] leading-none flex items-center justify-center">
              {inboxCount > 99 ? "99+" : inboxCount}
            </Badge>
          ) : null}
        </Button>

        {/* Notifications Bell */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 relative text-muted-foreground hover:text-foreground"
            >
              <Bell className="w-4 h-4" />
              {notifications.length > 0 ? (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
              ) : null}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72 text-xs">
            <DropdownMenuLabel className="font-bold flex justify-between items-center">
              <span>Notifications</span>
              {notifications.length > 0 ? (
                <Badge className="bg-primary text-primary-foreground text-[10px]">
                  {notifications.length} upcoming
                </Badge>
              ) : null}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="p-2 space-y-2 max-h-64 overflow-y-auto">
              {notifications.length === 0 ? (
                <p className="p-2 text-muted-foreground text-[11px]">
                  No upcoming site visits.
                </p>
              ) : (
                notifications.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    className="w-full text-left p-2 rounded bg-muted/40 text-xs hover:bg-muted/70"
                    onClick={() => navigate("/site_visits")}
                  >
                    <span className="font-bold text-foreground block">
                      {n.title}
                    </span>
                    <span className="text-muted-foreground text-[11px]">
                      {n.detail}
                    </span>
                  </button>
                ))
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Dark/Light Mode Toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 text-muted-foreground hover:text-foreground"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          title="Toggle Theme"
        >
          {theme === "dark" ? (
            <Sun className="w-4 h-4 text-amber-400" />
          ) : (
            <Moon className="w-4 h-4" />
          )}
        </Button>

        {/* User Profile Avatar / Logout */}
        <UserMenu />
      </div>

      <ScheduleVisitDrawer
        isOpen={scheduleOpen}
        onOpenChange={setScheduleOpen}
      />
    </header>
  );
};

export default Header;
